import React, { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { MapPin, Info, Clock } from "lucide-react";
import { motion } from "framer-motion";
import { supabase } from "@/lib/supabase";
import styles from "./LocationStep.module.css";
import { usePlacesAutocomplete } from "@/hooks/usePlacesAutocomplete";

interface LocationStepProps {
	onNext: (data: any) => void;
	onBack: () => void;
	initialData?: any;
}

const US_TIMEZONES = [
	{
		value: "America/New_York",
		label: "Eastern Time (EST/EDT)",
		states: [
			"NY",
			"FL",
			"GA",
			"NC",
			"SC",
			"VA",
			"WV",
			"OH",
			"PA",
			"NJ",
			"DE",
			"MD",
			"CT",
			"RI",
			"MA",
			"VT",
			"NH",
			"ME",
		],
	},
	{
		value: "America/Chicago",
		label: "Central Time (CST/CDT)",
		states: [
			"TX",
			"IL",
			"WI",
			"TN",
			"AL",
			"MS",
			"LA",
			"AR",
			"MO",
			"IA",
			"MN",
			"OK",
			"KS",
			"NE",
			"SD",
			"ND",
		],
	},
	{
		value: "America/Denver",
		label: "Mountain Time (MST/MDT)",
		states: ["CO", "WY", "MT", "UT", "NM"],
	},
	{
		value: "America/Phoenix",
		label: "Arizona Time (MST - No DST)",
		states: ["AZ"],
	},
	{
		value: "America/Los_Angeles",
		label: "Pacific Time (PST/PDT)",
		states: ["CA", "WA", "OR", "NV"],
	},
	{
		value: "America/Anchorage",
		label: "Alaska Time (AKST/AKDT)",
		states: ["AK"],
	},
	{ value: "Pacific/Honolulu", label: "Hawaii Time (HST)", states: ["HI"] },
	{
		value: "America/Boise",
		label: "Mountain Time - Idaho (MST/MDT)",
		states: ["ID"],
	},
];

export const LocationStep = ({
	onNext,
	onBack,
	initialData,
}: LocationStepProps) => {
	const [formData, setFormData] = useState({
		address: initialData?.address || "",
		city: initialData?.city || "",
		state: initialData?.state || "",
		zip: initialData?.zip || "",
		latitude: initialData?.latitude || null,
		longitude: initialData?.longitude || null,
		timezone: initialData?.timezone || "",
	});
	const [loading, setLoading] = useState(false);

	// Auto-suggest timezone based on state
	const suggestTimezone = (state: string) => {
		const stateCode = state.toUpperCase();
		const timezone = US_TIMEZONES.find((tz) => tz.states.includes(stateCode));
		return timezone?.value || "";
	};

	// Auto-update timezone when state changes
	useEffect(() => {
		if (formData.state && !formData.timezone) {
			const suggestedTimezone = suggestTimezone(formData.state);
			if (suggestedTimezone) {
				setFormData((prev) => ({
					...prev,
					timezone: suggestedTimezone,
				}));
			}
		}
	}, [formData.state, formData.timezone]);

	const addressInputRef = useRef<HTMLInputElement>(null);
	const { place: addressPlace } = usePlacesAutocomplete({
		inputRef: addressInputRef,
		types: ["address"],
		componentRestrictions: { country: "us" },
		fields: ["formatted_address", "address_components", "geometry"],
	});

	useEffect(() => {
		if (!addressPlace?.formatted_address || !addressPlace.address_components) return;
		let city = "";
		let state = "";
		let zip = "";
		for (const component of addressPlace.address_components) {
			const types = component.types;
			if (types.includes("locality")) city = component.long_name;
			else if (types.includes("administrative_area_level_1")) state = component.short_name;
			else if (types.includes("postal_code")) zip = component.long_name;
		}
		const suggestedTimezone = state ? suggestTimezone(state) : "";
		setFormData((prev) => ({
			...prev,
			address: addressPlace.formatted_address,
			city,
			state,
			zip,
			latitude: addressPlace.geometry?.location?.lat() || null,
			longitude: addressPlace.geometry?.location?.lng() || null,
			timezone: suggestedTimezone || prev.timezone,
		}));
	}, [addressPlace]);

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		if (
			!formData.address ||
			!formData.city ||
			!formData.state ||
			!formData.zip ||
			!formData.timezone
		) {
			alert("Please fill in all location fields including timezone");
			return;
		}

		setLoading(true);

		try {
			// Call the edge function to save progress
			const response = await fetch(
				`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/update-doula-profile`,
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
					},
					body: JSON.stringify({
						step: "location",
						data: {
							location: formData,
						},
					}),
				},
			);

			const result = await response.json();

			if (!response.ok) {
				throw new Error(result.error || "Failed to save location");
			}

			onNext(formData);
		} catch (error) {
			console.error("Error saving location:", error);
			alert("Failed to save location. Please try again.");
		} finally {
			setLoading(false);
		}
	};

	return (
		<motion.div
			initial={{ opacity: 0, y: 20 }}
			animate={{ opacity: 1, y: 0 }}
			transition={{ duration: 0.6 }}
			className={styles.wrapper}
		>
			{/* Progress Bar */}
			<div className={styles.progressSection}>
				<div className={styles.progressHeader}>
					<span className={styles.progressLabel}>Step 1 of 15</span>
					<span className={styles.progressLabel}>Location Information</span>
				</div>
				<div className={styles.progressTrack}>
					<div className={styles.progressFill} style={{ width: "10%" }} />
				</div>
			</div>

			{/* Main Card */}
			<Card className={styles.card}>
				<CardHeader className="text-center pb-4">
					<CardTitle className={styles.cardTitle}>
						<MapPin className="h-6 w-6 mr-2 text-newTheme-spruce" />
						Location Information
					</CardTitle>
					<p className={styles.cardSubtitle}>
						Where are you located? This helps parents find doulas in their area.
					</p>
				</CardHeader>

				<CardContent className="space-y-6">
					{/* Info Alert */}
					<div className={styles.infoAlert}>
						<div className="flex items-start">
							<Info className="h-5 w-5 text-newTheme-spruce mt-0.5 mr-2 flex-shrink-0" />
							<p className={styles.infoText}>
								<strong>Privacy Note:</strong> Only your city and state will be
								displayed to parents. The full address helps us calculate accurate
								distances for search results.
							</p>
						</div>
					</div>

					<form onSubmit={handleSubmit} className="space-y-6">
						{/* Street Address with Google Places */}
						<div>
							<Label htmlFor="address" className={styles.fieldLabel}>
								Street Address
							</Label>
							<div className="relative mt-1">
								<MapPin className={styles.inputIcon} />
								<Input
									ref={addressInputRef}
									id="address-input"
									type="text"
									value={formData.address}
									onChange={(e) =>
										setFormData({ ...formData, address: e.target.value })
									}
									placeholder="Start typing your address..."
									className="pl-10"
									required
								/>
							</div>
							<p className={styles.helperText}>
								Start typing and select from the dropdown suggestions
							</p>
						</div>

						{/* City, State, ZIP Grid */}
						<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
							<div>
								<Label htmlFor="city" className={styles.fieldLabel}>
									City
								</Label>
								<Input
									id="city"
									type="text"
									value={formData.city}
									onChange={(e) =>
										setFormData({ ...formData, city: e.target.value })
									}
									placeholder="City"
									className="mt-1"
									required
								/>
							</div>
							<div>
								<Label htmlFor="state" className={styles.fieldLabel}>
									State
								</Label>
								<Input
									id="state"
									type="text"
									value={formData.state}
									onChange={(e) =>
										setFormData({ ...formData, state: e.target.value })
									}
									placeholder="State"
									className="mt-1"
									required
								/>
							</div>
							<div>
								<Label htmlFor="zip" className={styles.fieldLabel}>
									ZIP Code
								</Label>
								<Input
									id="zip"
									type="text"
									value={formData.zip}
									onChange={(e) =>
										setFormData({ ...formData, zip: e.target.value })
									}
									placeholder="ZIP Code"
									className="mt-1"
									required
								/>
							</div>
						</div>

						{/* Timezone Selection */}
						<div>
							<Label className={styles.fieldLabel}>Time Zone</Label>
							<div className="relative mt-1">
								<Clock className={`${styles.inputIcon} z-10`} />
								<Select
									value={formData.timezone}
									onValueChange={(value) =>
										setFormData({ ...formData, timezone: value })
									}
									required
								>
									<SelectTrigger className="pl-10 bg-newTheme-white">
										<SelectValue placeholder="Select your time zone..." />
									</SelectTrigger>
									<SelectContent className="bg-newTheme-white">
										{US_TIMEZONES.map((timezone) => (
											<SelectItem key={timezone.value} value={timezone.value}>
												{timezone.label}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>
							<p className={styles.helperText}>
								This will be auto-selected based on your state, but you can
								change it if needed
							</p>
						</div>

						{/* Navigation Buttons */}
						<div className={styles.navRow}>
							<Button
								type="button"
								variant="outline"
								onClick={onBack}
								className={styles.backButton}
							>
								Back
							</Button>
							<Button
								type="submit"
								disabled={loading}
								className={styles.submitButton}
							>
								{loading ? (
									<div className="flex items-center">
										<div className={styles.spinner} />
										Saving...
									</div>
								) : (
									"Continue"
								)}
							</Button>
						</div>
					</form>
				</CardContent>
			</Card>
		</motion.div>
	);
};
